import React, { useState, useEffect, useMemo } from "react";
import { getAllHrSettings, saveHrSettings, parseIcsFile, computeJahresarbeitszeit, calculateWorkingDays } from "../services/azureHrService";
import { TEAM_CATEGORIES } from "../services/azureTeamService";

const DEFAULT_TAGESARBEITSZEIT = 8.2;

const HrSettingsModal = ({ onClose }) => {
  const [hrData, setHrData] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());

  // Form state for the selected year
  const [tagesarbeitszeit, setTagesarbeitszeit] = useState(DEFAULT_TAGESARBEITSZEIT);
  const [feiertage, setFeiertage] = useState([]);
  const [betriebsferien, setBetriebsferien] = useState([]);
  const [newFeiertagDate, setNewFeiertagDate] = useState("");
  const [newFeiertagName, setNewFeiertagName] = useState("");
  const [newBetriebsferienStart, setNewBetriebsferienStart] = useState("");
  const [newBetriebsferienEnd, setNewBetriebsferienEnd] = useState("");
  const [ferientageByCategory, setFerientageByCategory] = useState({});

  const availableYears = [];
  const currentYear = new Date().getFullYear();
  for (let y = currentYear - 2; y <= currentYear + 2; y++) {
    availableYears.push(y);
  }

  useEffect(() => {
    loadAllSettings();
  }, []);

  useEffect(() => {
    const yearData = hrData[selectedYear];
    if (yearData) {
      setTagesarbeitszeit(yearData.tagesarbeitszeit || DEFAULT_TAGESARBEITSZEIT);
      setFeiertage(yearData.feiertage || []);
      setBetriebsferien(yearData.betriebsferien || []);
      setFerientageByCategory(yearData.ferientageByCategory || {});
    } else {
      setTagesarbeitszeit(DEFAULT_TAGESARBEITSZEIT);
      setFeiertage([]);
      setBetriebsferien([]);
      setFerientageByCategory({});
    }
  }, [selectedYear, hrData]);

  // Auto-calculate Jahresarbeitszeit
  const jahresarbeitszeit = useMemo(() => {
    return computeJahresarbeitszeit(selectedYear, feiertage, tagesarbeitszeit);
  }, [selectedYear, feiertage, tagesarbeitszeit]);

  // Betriebsferien werden vorgearbeitet: Jahresarbeitszeit bleibt, aber sie wird auf
  // weniger effektiven Arbeitstagen geleistet → höhere effektive Tagesarbeitszeit.
  const arbeitszeitInfo = useMemo(() => {
    const yearStart = `${selectedYear}-01-01`;
    const yearEnd = `${selectedYear}-12-31`;
    const workingDays = calculateWorkingDays(yearStart, yearEnd, feiertage, []);
    const effectiveWorkingDays = calculateWorkingDays(yearStart, yearEnd, feiertage, betriebsferien);
    const betriebsferienOnWorkdays = workingDays - effectiveWorkingDays;
    const effektiveTagesarbeitszeit = effectiveWorkingDays > 0
      ? jahresarbeitszeit / effectiveWorkingDays
      : tagesarbeitszeit;
    return {
      workingDays,
      effectiveWorkingDays,
      betriebsferienOnWorkdays,
      effektiveTagesarbeitszeit,
    };
  }, [selectedYear, feiertage, betriebsferien, jahresarbeitszeit, tagesarbeitszeit]);

  const loadAllSettings = async () => {
    setLoading(true);
    try {
      const settings = await getAllHrSettings();
      const map = {};
      settings.forEach((s) => {
        map[s.year] = s;
      });
      setHrData(map);
    } catch (err) {
      console.error("Failed to load HR settings:", err);
      setHrData({});
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      await saveHrSettings(selectedYear, {
        tagesarbeitszeit,
        feiertage,
        betriebsferien,
        ferientageByCategory,
      });
      setHrData((prev) => ({
        ...prev,
        [selectedYear]: {
          year: String(selectedYear),
          tagesarbeitszeit,
          jahresarbeitszeit,
          feiertage,
          betriebsferien,
          ferientageByCategory,
        },
      }));
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const addFeiertag = () => {
    if (!newFeiertagDate) return;
    if (feiertage.some((f) => f.date === newFeiertagDate)) return;

    const newEntry = {
      date: newFeiertagDate,
      name: newFeiertagName || "Feiertag",
    };
    setFeiertage([...feiertage, newEntry].sort((a, b) => a.date.localeCompare(b.date)));
    setNewFeiertagDate("");
    setNewFeiertagName("");
  };

  const removeFeiertag = (date) => {
    setFeiertage(feiertage.filter((f) => f.date !== date));
  };

  const handleIcsUpload = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const content = e.target.result;
        const parsed = parseIcsFile(content, selectedYear);

        if (parsed.length === 0) {
          setError(`Keine Feiertage für ${selectedYear} in der ICS-Datei gefunden`);
          return;
        }

        // Merge with existing, avoiding duplicates
        const existingDates = new Set(feiertage.map((f) => f.date));
        const newEntries = parsed.filter((p) => !existingDates.has(p.date));
        setFeiertage(
          [...feiertage, ...newEntries].sort((a, b) => a.date.localeCompare(b.date))
        );
      } catch (err) {
        setError("Fehler beim Lesen der ICS-Datei: " + err.message);
      }
    };
    reader.readAsText(file);
    event.target.value = "";
  };

  const addBetriebsferien = () => {
    if (!newBetriebsferienStart) return;
    const end = newBetriebsferienEnd || newBetriebsferienStart;

    const newDates = [];
    const current = new Date(newBetriebsferienStart);
    const endDate = new Date(end);
    while (current <= endDate) {
      const dayOfWeek = current.getDay();
      if (dayOfWeek >= 1 && dayOfWeek <= 5) {
        const dateStr = current.toISOString().split("T")[0];
        if (!betriebsferien.includes(dateStr)) {
          newDates.push(dateStr);
        }
      }
      current.setDate(current.getDate() + 1);
    }

    setBetriebsferien([...betriebsferien, ...newDates].sort());
    setNewBetriebsferienStart("");
    setNewBetriebsferienEnd("");
  };

  const removeBetriebsferientag = (date) => {
    setBetriebsferien(betriebsferien.filter((d) => d !== date));
  };

  const formatDate = (dateStr) => {
    const d = new Date(dateStr);
    const days = ["So", "Mo", "Di", "Mi", "Do", "Fr", "Sa"];
    return `${days[d.getDay()]} ${d.getDate()}.${d.getMonth() + 1}.`;
  };

  return (
    <>
      <div className="overlay" onClick={onClose}></div>
      <div className="hr-settings-modal">
        <div className="modal-header">
          <h2>HR Einstellungen</h2>
          <button className="btn-close" onClick={onClose}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="18" y1="6" x2="6" y2="18"/>
              <line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
        </div>

        <div className="modal-body">
          {error && (
            <div className="error-message" onClick={() => setError(null)}>
              {error}
            </div>
          )}

          {/* Year Tabs */}
          <div className="year-tabs">
            {availableYears.map((year) => (
              <button
                key={year}
                className={`tab-btn ${selectedYear === year ? "active" : ""}`}
                onClick={() => setSelectedYear(year)}
              >
                {year}
              </button>
            ))}
          </div>

          {loading ? (
            <div className="loading-indicator">Lade HR-Daten...</div>
          ) : (
            <div className="hr-form">
              {/* Tagesarbeitszeit & Jahresarbeitszeit */}
              <div className="hr-section">
                <h3>Arbeitszeit</h3>
                <div className="form-row">
                  <div className="form-group">
                    <label>Tagesarbeitszeit (h)</label>
                    <input
                      type="number"
                      value={tagesarbeitszeit}
                      onChange={(e) => setTagesarbeitszeit(parseFloat(e.target.value) || 0)}
                      step="0.1"
                      min="0"
                      max="24"
                    />
                  </div>
                  <div className="form-group info-text">
                    <label>Jahresarbeitszeit (berechnet)</label>
                    <span className="computed-value">
                      {jahresarbeitszeit.toFixed(1)}h
                    </span>
                    <span className="computed-detail">
                      {arbeitszeitInfo.workingDays} Arbeitstage (Werktage &minus; Feiertage) &times; {tagesarbeitszeit}h
                    </span>
                    {arbeitszeitInfo.betriebsferienOnWorkdays > 0 && (
                      <span className="computed-detail computed-detail-note">
                        Betriebsferien ({arbeitszeitInfo.betriebsferienOnWorkdays} Werktage) werden vorgearbeitet:
                        dieselbe Jahresarbeitszeit wird auf {arbeitszeitInfo.effectiveWorkingDays} effektive Arbeitstage verteilt →
                        <strong> effektive Tagesarbeitszeit {arbeitszeitInfo.effektiveTagesarbeitszeit.toFixed(2)}h</strong>.
                      </span>
                    )}
                  </div>
                </div>
              </div>

              {/* Feiertage */}
              <div className="hr-section">
                <h3>Feiertage ({feiertage.length})</h3>
                <div className="date-add-row">
                  <input
                    type="date"
                    value={newFeiertagDate}
                    onChange={(e) => setNewFeiertagDate(e.target.value)}
                    min={`${selectedYear}-01-01`}
                    max={`${selectedYear}-12-31`}
                  />
                  <input
                    type="text"
                    value={newFeiertagName}
                    onChange={(e) => setNewFeiertagName(e.target.value)}
                    placeholder="Name (z.B. Neujahr)"
                    className="feiertag-name-input"
                  />
                  <button
                    type="button"
                    className="btn btn-sm btn-primary"
                    onClick={addFeiertag}
                    disabled={!newFeiertagDate}
                  >
                    +
                  </button>
                </div>
                <div className="ics-upload-row">
                  <label className="ics-upload-label">
                    <input
                      type="file"
                      accept=".ics"
                      onChange={handleIcsUpload}
                      style={{ display: "none" }}
                    />
                    <span className="btn btn-sm btn-secondary">
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                        <polyline points="17,8 12,3 7,8"/>
                        <line x1="12" y1="3" x2="12" y2="15"/>
                      </svg>
                      ICS importieren
                    </span>
                  </label>
                </div>
                <div className="date-list">
                  {feiertage.map((ft) => (
                    <div key={ft.date} className="date-item feiertag-item">
                      <span className="date-item-date">{formatDate(ft.date)}</span>
                      <span className="date-item-name">{ft.name}</span>
                      <button
                        type="button"
                        className="btn-icon btn-delete"
                        onClick={() => removeFeiertag(ft.date)}
                      >
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <line x1="18" y1="6" x2="6" y2="18"/>
                          <line x1="6" y1="6" x2="18" y2="18"/>
                        </svg>
                      </button>
                    </div>
                  ))}
                  {feiertage.length === 0 && (
                    <span className="no-data-hint">Keine Feiertage definiert</span>
                  )}
                </div>
              </div>

              {/* Betriebsferien */}
              <div className="hr-section">
                <h3>Betriebsferien ({betriebsferien.length} Tage)</h3>
                <div className="date-add-row">
                  <input
                    type="date"
                    value={newBetriebsferienStart}
                    onChange={(e) => setNewBetriebsferienStart(e.target.value)}
                    min={`${selectedYear}-01-01`}
                    max={`${selectedYear}-12-31`}
                  />
                  <input
                    type="date"
                    value={newBetriebsferienEnd}
                    onChange={(e) => setNewBetriebsferienEnd(e.target.value)}
                    min={newBetriebsferienStart || `${selectedYear}-01-01`}
                    max={`${selectedYear}-12-31`}
                  />
                  <button
                    type="button"
                    className="btn btn-sm btn-primary"
                    onClick={addBetriebsferien}
                    disabled={!newBetriebsferienStart}
                  >
                    +
                  </button>
                </div>
                <div className="date-list">
                  {betriebsferien.map((date) => (
                    <div key={date} className="date-item">
                      <span>{formatDate(date)}</span>
                      <button
                        type="button"
                        className="btn-icon btn-delete"
                        onClick={() => removeBetriebsferientag(date)}
                      >
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <line x1="18" y1="6" x2="6" y2="18"/>
                          <line x1="6" y1="6" x2="18" y2="18"/>
                        </svg>
                      </button>
                    </div>
                  ))}
                  {betriebsferien.length === 0 && (
                    <span className="no-data-hint">Keine Betriebsferien definiert</span>
                  )}
                </div>
              </div>

              {/* Ferientage pro Kategorie */}
              <div className="hr-section">
                <h3>Ferientage pro Kategorie (100% Pensum)</h3>
                <div className="category-holidays-grid">
                  {TEAM_CATEGORIES.map((cat) => (
                    <div key={cat} className="form-group category-holiday-item">
                      <label>{cat}</label>
                      <input
                        type="number"
                        value={ferientageByCategory[cat] || ""}
                        onChange={(e) =>
                          setFerientageByCategory((prev) => ({
                            ...prev,
                            [cat]: parseFloat(e.target.value) || 0,
                          }))
                        }
                        placeholder="Tage"
                        min="0"
                        step="0.5"
                      />
                    </div>
                  ))}
                </div>
              </div>

              {/* Save Button */}
              <div className="modal-actions">
                <button type="button" className="btn btn-secondary" onClick={onClose}>
                  Schliessen
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleSave}
                  disabled={saving}
                >
                  {saving ? "Speichern..." : "Speichern"}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default HrSettingsModal;
